import React, { useEffect, useState, useRef } from 'react';
import { FadeIn, StaggerContainer, StaggerItem } from '../components/ScrollAnimation';
import { projectAPI } from '../utils/api';
import { useGesture } from '../context/GestureContext';
import Pagination from '../components/Pagination';
import DetailModal from '../components/DetailModal';

const Projects = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1
  });
  const [selectedProject, setSelectedProject] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { lastGesture, cardNavActive } = useGesture();
  const lastProcessedTime = useRef(0);
  const lastOpenPalmTime = useRef(0);

  const fetchProjects = React.useCallback((page) => {
    setLoading(true);
    projectAPI.getAll(page, 6)
      .then((res) => {
        setProjects(Array.isArray(res.data) ? res.data : []);
        setPagination({
          currentPage: res.currentPage || 1,
          totalPages: res.totalPages || 1
        });
        setFocusedIndex(-1); // Reset focus on page change
      })
      .catch((err) => {
        console.error('Failed to fetch projects:', err);
        setLoadError('Failed to load projects. Make sure the backend is running.');
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    // Track page view
    projectAPI.trackView('projects-page').catch(err => console.error(err));
    fetchProjects(1);
  }, [fetchProjects]);

  const handlePageChange = (newPage) => {
    fetchProjects(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCardClick = (project) => {
    setSelectedProject(project);
    setIsModalOpen(true);
  };
  useEffect(() => {
    if (lastGesture.time > lastProcessedTime.current && projects.length > 0) {
      lastProcessedTime.current = lastGesture.time;

      // ONLY handle card focus if Navigation Mode is toggled to CARDS
      if (!cardNavActive) return;

      let newIndex = focusedIndex;
      switch (lastGesture.name) {
        case 'swipe_left':
          newIndex = focusedIndex === -1 ? 0 : Math.min(projects.length - 1, focusedIndex + 1);
          break;
        case 'swipe_right':
          newIndex = focusedIndex === -1 ? 0 : Math.max(0, focusedIndex - 1);
          break;
        case 'rock_on':
          const now = Date.now();
          if (now - lastOpenPalmTime.current < 1500) return;

          if (isModalOpen) {
            setIsModalOpen(false);
            lastOpenPalmTime.current = now;
          } else if (cardNavActive && focusedIndex !== -1 && projects[focusedIndex]) {
            handleCardClick(projects[focusedIndex]);
            lastOpenPalmTime.current = now;
          }
          return;
        default:
          return;
      }

      setFocusedIndex(newIndex);

      // Auto-scroll the focused card into center view
      setTimeout(() => {
        const element = document.getElementById(`project-card-${newIndex}`);
        if (element) {
          element.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'center'
          });
        }
      }, 50);
    }
  }, [lastGesture, projects, cardNavActive, focusedIndex, isModalOpen]);

  return (
    <div className="page-container">
      <h1 className="page-title">Our Projects</h1>
      <div className="page-content">
        <FadeIn>
          <div className="section">
            <h2>Featured Projects</h2>
            <p>
              Explore our innovative AI projects that are shaping the future of technology
              and making a real-world impact.
            </p>
          </div>
        </FadeIn>

        <StaggerContainer staggerDelay={0.15}>
          <div className="card-grid" id="card-grid-section">
            {loading && (
              <div className="card">
                <h3>Loading projects...</h3>
                <p>Fetching the latest project data.</p>
              </div>
            )}
            {!loading && loadError && (
              <div className="card">
                <h3>Unable to load projects</h3>
                <p>{loadError}</p>
              </div>
            )}
            {!loading && !loadError && projects.length === 0 && (
              <div className="card">
                <h3>No projects yet</h3>
                <p>Run the seed script to load sample project data.</p>
              </div>
            )}
            {projects.map((project, index) => (
              <StaggerItem key={project._id}>
                <div
                  id={`project-card-${index}`}
                  className={`card ${focusedIndex === index ? 'focused' : ''}`}
                  onClick={() => handleCardClick(project)}
                >
                  <div className="card-modal-indicator">ℹ️ Info</div>
                  <h3>{project.icon} {project.name}</h3>
                  <p>{project.description}</p>
                  <p><strong>Status:</strong> {project.status}</p>
                  <p><strong>{project.metricLabel}:</strong> {project.metricValue}</p>
                </div>
              </StaggerItem>
            ))}
          </div>
        </StaggerContainer>

        <Pagination
          currentPage={pagination.currentPage}
          totalPages={pagination.totalPages}
          onPageChange={handlePageChange}
        />

        <DetailModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          data={selectedProject}
          type="project"
        />

        <FadeIn direction="up" delay={0.5}>
          <div className="section">
            <h2>Open Source Contributions</h2>
            <ul>
              <li>PyTorch-Vision-Extensions - Enhanced computer vision utilities</li>
              <li>NLP-Toolkit - Comprehensive natural language processing library</li>
              <li>AI-Ethics-Framework - Tools for responsible AI development</li>
              <li>Quantum-ML-Lib - Quantum machine learning implementations</li>
            </ul>
          </div>
        </FadeIn>

        <FadeIn direction="up" delay={0.6}>
          <div className="section">
            <h2>Industry Partnerships</h2>
            <p>
              Our projects are developed in collaboration with industry leaders to ensure
              practical applicability and real-world impact. Partners include Fortune 500
              companies, startups, and government agencies.
            </p>
          </div>
        </FadeIn>
      </div>
    </div>
  );
};

export default Projects;
